import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { PublicRecordApi } from 'src/app/_shared/api/public-record.api';
import { ReportsApi } from 'src/app/_shared/api/reports.api';
import { environment } from 'src/environments/environment';
import { ProspektrApi } from '../_shared/api/prospektr.api';
import { StripeApi } from '../_shared/api/stripe.api';
import { UserState } from '../_shared/state/user.state';
import { AppState } from '../app.state';
import { CmaState } from '../cma/cma.state';
import { ReportsState } from './reports.state';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  upgradeSnackbar: MatSnackBarRef<any>;
  listingData: any;

  constructor(
    private userState: UserState,
    private reportsState: ReportsState,
    private ReportsApi: ReportsApi,
    private snackbar: MatSnackBar,
    private sanitizer: DomSanitizer,
    private stripeApi: StripeApi,
    private prospektrApi: ProspektrApi,
    private cmaState: CmaState,
    private appState: AppState,
    private publicRecordApi: PublicRecordApi
  ) { }

  getPlanStatus() {
    const roles = this.userState?.userDetails?.roles;
    if (!roles || (!roles.includes('agent') && !roles.includes('loanOfficer'))) {
      return;
    }
    if (!this.appState?.companyInfoValue?.appConfig?.planSubscription?.isEnabled) {
      return;
    }

    this.ReportsApi.getPlanStatus().subscribe(
      (res: any) => {
        if (res && res.statusCode == 200 && res.status == 'OK' && res.data) {
          this.userState.getUserPlanStatusValue = res.data;
        }
      },
      (err: any) => {
        console.log('getPlanStatus err : ', err);
      }
    );
  }

  getPlanStatusNew() {
    const roles = this.userState?.userDetails?.roles;

    if (!roles || (!roles.includes('agent') && !roles.includes('loanOfficer'))) {
      return EMPTY; // RxJS empty observable
    }

    return this.ReportsApi.getPlanStatus().pipe(
      map((res: any) => {
        if (res?.statusCode === 200 && res.status === 'OK' && res.data) {
          this.userState.getUserPlanStatusValue = res.data;
          return res.data;
        }
        return null;
      }),
      catchError((err) => {
        console.error('getPlanStatus err:', err);
        return throwError(() => err);
      })
    );
  }

  getPlans() {
    return this.prospektrApi.getPlans({}).pipe(
      tap((res: any) => {
        if (res && res.statusCode === 200 && res.status === 'OK' && res.data) {
          this.userState.plans.next(res.data); // Update the plans state
          this.userState.totalPlans.next(res.data.length); // Update total plans count
        }
      }),
      catchError((error) => {
        // Handle errors here (if any)
        console.error('getPlans error:', error);
        return of(null);
      })
    );
  }

  getConnectedUsers() {
    this.prospektrApi.getConnectedUsers().subscribe(
      (res: any) => {
        this.userState.connectedUsers.next(res.userData);
      },
      (err: any) => {
        console.log('getConnectedUsers err : ', err);
      }
    );
  }

  getConnectedAgents() {
    this.prospektrApi.getConnectedAgents().subscribe(
      (res: any) => {
        this.userState.connectedAgents.next(res.userData);
      },
      (err: any) => {
        console.log('getConnectedAgents err : ', err);
      }
    );
  }

  getSavedCards() {
    this.stripeApi.getSavedCards().subscribe(
      (response) => {
        if (
          response &&
          response.status == 'OK' &&
          response.statusCode == 200 &&
          response.data &&
          response.data.length > 0
        ) {
          this.reportsState.isPaymentCardAdded = true;
        } else {
          if (this.reportsState.isPaymentCardAdded) {
            this.reportsState.isPaymentCardAdded = false;
          }
        }
      },
      (error) => {
        if (this.reportsState.isPaymentCardAdded) {
          this.reportsState.isPaymentCardAdded = false;
        }
        console.error('Error fetching saved cards:', error);
      }
    );
  }

  downloadReportAsHtml(params) {
    this.reportsState.isLoadingReport = true;

    this.ReportsApi.downloadReportAsHtml(params).subscribe(
      (res: any) => {
        if (res && res.status == 'success' && res.data) {
          this.reportsState.createReportResponseValue = res;
        } else {
          this.reportsState.createReportResponseValue = res;
        }
        this.reportsState.isLoadingReport = false;
      },
      (err: any) => {
        console.log('downloadReportAsHtml err : ', err);
        this.reportsState.createReportResponseValue = err;
        this.reportsState.isLoadingReport = false;
      }
    );
  }

  downloadReportAsPdf(params) {
    this.reportsState.isDownloadingReport = true;

    this.ReportsApi.downloadReport(params).subscribe(
      (res: any) => {
        let url: SafeResourceUrl;
        const file = new Blob([res], { type: 'application/pdf' });
        let pdfUrl = URL.createObjectURL(file);
        url = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
        this.reportsState.valueReportPdfUrl = url;
      },
      (err: any) => {
        console.log('downloadReport err : ', err);
        this.reportsState.isDownloadingReport = false;
      }
    );
  }

  downloadReport(params: any, name: string): void {
    this.reportsState.isLoadingReport = true;
    this.reportsState.isDownloadingReport = true;

    this.ReportsApi.downloadReport(params).subscribe(
      (res: any) => {

        if (res instanceof Blob && res.size > 0) {
          const downloadURL = URL.createObjectURL(res);
          const link = document.createElement('a');

          link.href = downloadURL;
          link.download = `${name}.pdf`;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          URL.revokeObjectURL(downloadURL);
        } else {
          console.warn('The download response is empty or is not a valid Blob.', res);
        }

        this.reportsState.isLoadingReport = false;
        this.reportsState.isDownloadingReport = false;
      },
      (err: any) => {
        console.error('downloadReport error:', err);

        this.reportsState.isLoadingReport = false;
        this.reportsState.isDownloadingReport = false;
      }
    );
  }

  createReport(params) {
    this.reportsState.isLoadingReport = true;
    this.ReportsApi.createReport(params).subscribe(
      (res: any) => {
        this.reportsState.createReportResponseValue = res;
      },
      (err: any) => {
        this.reportsState.createReportResponseValue = err;
      }
    );


  }

  createResearchStoreReport(params) {
    return this.ReportsApi.createResearchStoreReport(params);
  }

  createFinalValueRSReport(params) {
    return this.ReportsApi.createFinalValueRSReport(params);
  }

  createAppraiseRSReport(params) {
    return this.ReportsApi.createAppraiseRSReport(params);
  }

  fetchResearchStoreReport(params) {
    return this.ReportsApi.fetchResearchStoreReport(params);
  }

  fetchAppraiserReport(params) {
    return this.ReportsApi.fetchAppraiserReport(params);
  }

  updateResearchStoreReport(params) {
    return this.ReportsApi.updateResearchStoreReport(params);
  }

  sendPreListingApproval(params) {
    return this.ReportsApi.sendPreListingApproval(params);
  }

  updateReport(params) {
    this.reportsState.isDownloadingReport = true;

    this.ReportsApi.updateReport(params).subscribe(
      (res: any) => {
        this.reportsState.createReportResponseValue = res;
      },
      (err: any) => {
        this.reportsState.createReportResponseValue = err;
      }
    );
  }

  refreshReport(params) {
    this.reportsState.isDownloadingReport = true;

    this.ReportsApi.refreshReport(params).subscribe(
      (res: any) => {
        this.reportsState.createReportResponseValue = res;
      },
      (err: any) => {
        this.reportsState.createReportResponseValue = err;
      }
    );
  }

  // applyCode(params) {
  //   this.prospektrApi.applyCode(params).subscribe(
  //     (res: any) => {
  //       this.userState.couponValue.next(res.data);
  //       if (res && res.status && res.status === 'OK' && res.data) {
  //         this.userState.couponValue.next(res.data);
  //       } else {
  //         this.userState.couponValue.next(null);
  //       }
  //     },
  //     (err: any) => {
  //       console.log('Error: ', err);
  //     }
  //   );
  // }

  getAllReports(params) {
    this.reportsState.isLoadingReport = true;

    this.ReportsApi.getAllReports(params).subscribe(
      (res: any) => {
        if (res && res.status && res.status === 'OK' && res.data) {
          this.reportsState.reportsListValue = res.data && res.data.paginatedResults ? res.data.paginatedResults : [];
          this.reportsState.reportsCountValue = res.data && res.data.totalRecords ? res.data.totalRecords : 0;
          if (this.reportsState.filterValValue) {
            this.reportsState.filterValValue.currentPageSize = this.reportsState.reportsListValue.length;
          }
          // this.reportsState.filterValValue.currentPageSize = this.reportsState.reportsListValue.length;
        } else {
          this.reportsState.reportsListValue = [];
          this.reportsState.reportsCountValue = 0;
          if (this.reportsState.filterValValue) {
            this.reportsState.filterValValue.currentPageSize = 0;
          }
          // this.reportsState.filterValValue.currentPageSize = 0;
        }
        this.reportsState.isLoadingReport = false;
      },
      (err: any) => {
        console.log('getAllReports err : ', err);
        this.reportsState.isLoadingReport = false;
      }
    );
  }

  getStatisticsData(params) {
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.ReportsApi.getStatisticsData(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            if (res && res.data) {
              this.reportsState.statisticsDataListValue = res.data.data;
              this.reportsState.statiticsTotalCountValue = res.data.totalRecord;
            } else {
              this.reportsState.statisticsDataListValue = [];
              this.reportsState.statiticsTotalCountValue = 0;
            }
          },
          (err) => {
            this.reportsState.statisticsDataListValue = [];
            this.reportsState.statiticsTotalCountValue = 0;
          }
        );
    });
  }

  getReportById(params) {
    this.reportsState.isLoadingReport = true;

    this.ReportsApi.getReportById(params).subscribe(
      (res: any) => {
        if (res && res.status && res.status === 'OK' && res.data) {
          this.reportsState.reportsValue = res.data ? res.data : null;
        } else {
          this.reportsState.reportsValue = null;
        }
      },
      (err: any) => {
        console.log('getReportById err : ', err);
        this.reportsState.isLoadingReport = false;
      }
    );
  }

  getSharedReport(params: { id: string; type: string }): Observable<any> {
    this.reportsState.isLoadingReport = true;
    return this.ReportsApi.getSharedReport(params).pipe(
      tap((res: any) => {
        if (res && res.status === 'OK' && res.data) {
          this.reportsState.reportsValue = res.data;
        } else {
          this.reportsState.reportsValue = null;
        }
        this.reportsState.isLoadingReport = false;
      }),
      catchError((err) => {
        console.log('getReportById err : ', err);
        this.reportsState.isLoadingReport = false;
        return of(null); // return null observable on error
      })
    );
  }

  // getListingDetails(params, flag = null) {
  //   if (flag == 'cma') this.cmaState.isLoadingCreateReport = true;
  //   else this.reportsState.isLoadingCreateReport = true;

  //   // listingData = null;
  //   this.ReportsApi.getListingDetails(params).subscribe(
  //     (res: any) => {
  //       if (res && res.Status && res.Status.Message === 'Success' && res.ListingDetails) {
  //         if (res.ImageURLs && res.ImageURLs.length > 0) {
  //           res.ListingDetails[0].imgUrl = res.ImageURLs[0];
  //         }
  //         // listingData = res.ListingDetails[0];
  //       }
  //       this.getProperty(params, flag);
  //     },
  //     (err: any) => {
  //       this.getProperty(params, flag);
  //       console.log('getListingDetails : err : ', err);
  //     }
  //   );
  // }

  searchPropertyDetails(params, flag = null) {
    if (flag == 'cma') this.cmaState.isLoadingCreateReport = true;
    else this.reportsState.isLoadingCreateReport = true;
    this.ReportsApi.searchPropertyDetails(params).subscribe(
      (res: any) => {
        if (res && res.Status && res.Status.Message === 'Success' && res.Data) {
          if (flag == 'cma') {
            this.cmaState.listPropertySetValue = res.Data.Properties;
            this.cmaState.listPropertySetCount = res.Data.Count;
          } else {
            this.reportsState.listPropertySetValue = res.Data.Properties;
            this.reportsState.listPropertySetCount = res.Data.Count;
          }
        } else {
          this.openSnackbar('Property not found', false);
          this.reportsState.listPropertySetValue = null;
          this.reportsState.listPropertySetCount = null;  // to clear an old search
        }
        if (flag == 'cma') this.cmaState.isLoadingCreateReport = false;
        else this.reportsState.isLoadingCreateReport = false;
      },
      (err: any) => {
        console.log('searchPropertyDetails : err : ', err);
        this.openSnackbar('Property not found', false);
        if (flag == 'cma') this.cmaState.isLoadingCreateReport = false;
        else this.reportsState.isLoadingCreateReport = false;
      }
    );
  }

  getProperty(propertyObj, flag = null) {
    if (flag == 'cma') this.cmaState.isLoadingCreateReport = true;
    else this.reportsState.isLoadingCreateReport = true;

    this.publicRecordApi.getPropertyDetails(propertyObj).subscribe(
      (res) => {
        if (res && res.status && res.statusCode === 200 && res.data) {
          this.formatPropertyData(res.data, flag);
        } else {
          this.openSnackbar('Property not found', false);
        }
      },
      (err: any) => {
        console.log('getPropertyDetails : err : ', err);
        this.openSnackbar('Property not found', false);
        if (flag == 'cma') this.cmaState.isLoadingCreateReport = false;
        else this.reportsState.isLoadingCreateReport = false;
      }
    );
  }

  deleteReport(reportId): Observable<{ isLoading?: boolean; data?: any }> {
    this.reportsState.isLoadingReport = true;
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.ReportsApi.deleteReport({ reportId: reportId })
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            this.reportsState.isLoadingReport = false;
            observer.next({ data: res ? res : null });
          },
          (err) => {
            this.reportsState.isLoadingReport = false;
            observer.next({ data: null });
          }
        );
    });
  }

  formatPropertyData(data, flag) {
    let propertyData;
    let listingData;

    if (data.listing) {
      listingData = data.listing;
    }
    if (data.basic) {
      propertyData = data.basic;
    }
    if (!propertyData && !listingData) {
      this.openSnackbar('Property not found', false);
      return;
    }

    // if (data.imageList) {
    //   propertyData['imgUrl'] = data.imageList[0];
    // }

    let valueTarget = null;
    if (data.basic) {
      let basicData = data.basic;
      if (basicData?.Lst_StandardStatus == 'Active') {
        valueTarget = basicData.Lst_ListPrice;
      } else if (basicData?.Lst_StandardStatus == 'Sold' && basicData?.Lst_CloseDate) {
        if (this.calculateDateDifference(basicData.Lst_CloseDate) < 183) {
          valueTarget = basicData.Lst_ClosePrice;
        }
      }
    }

    let propertyDetails = {
      Address:
        listingData && listingData.Address
          ? listingData.Address
          : propertyData && propertyData.Address
            ? propertyData.Address
            : propertyData['StreetAddress'] +
            ', ' +
            propertyData['City'] +
            ', ' +
            propertyData['StateOrProvince'] +
            ' ' +
            propertyData['PostalCode'],

      numberOfImages:
        data?.imageList && data?.imageList != 'No image URL available for this property' ? data.imageList.length : 0,
      imgUrl:
        environment.listingImageBaseUrl +
        '?ListingId=' +
        (propertyData && propertyData?.Lst_ListingId
          ? propertyData.Lst_ListingId
          : listingData && listingData.ListingId
            ? listingData.ListingId
            : 'null') +
        '&imageName=1&height=219',

      ListingId:
        listingData && listingData.ListingId
          ? listingData.ListingId
          : propertyData && propertyData?.Lst_ListingId
            ? propertyData.Lst_ListingId
            : null,
      PMXPropertyId:
        listingData && listingData.PMXPropertyId
          ? listingData.PMXPropertyId
          : propertyData && propertyData.PMXPropertyId
            ? propertyData.PMXPropertyId
            : null,

      PropertySubType:
        listingData && listingData.PropertySubType
          ? listingData.PropertySubType
          : propertyData && propertyData.PropertySubType
            ? propertyData.PropertySubType
            : null,
      LivingArea:
        listingData && listingData.LivingArea != ''
          ? listingData.LivingArea
          : propertyData && propertyData.LivingArea
            ? propertyData.LivingArea
            : null,
      YearBuilt:
        listingData && listingData.YearBuilt != ''
          ? listingData.YearBuilt
          : propertyData && propertyData.YearBuilt
            ? propertyData.YearBuilt
            : null,
      BedroomsTotal:
        listingData && listingData.BedroomsTotal != ''
          ? listingData.BedroomsTotal
          : propertyData && propertyData.BedroomsTotal
            ? propertyData.BedroomsTotal
            : null,
      BathroomsTotalInteger:
        listingData && listingData.BathroomsTotalInteger != ''
          ? listingData.BathroomsTotalInteger
          : propertyData && propertyData.BathroomsTotalInteger
            ? propertyData.BathroomsTotalInteger
            : null,
      LotSizeSquareFeet:
        listingData && listingData.LotSizeSquareFeet != ''
          ? listingData.LotSizeSquareFeet
          : propertyData && propertyData.LotSizeSquareFeet
            ? propertyData.LotSizeSquareFeet
            : null,

      Parking:
        listingData && listingData.ParkingTotal != ''
          ? listingData.ParkingTotal
          : propertyData && propertyData.ParkingSpaces != ''
            ? propertyData.ParkingSpaces
            : null,

      Stories:
        listingData && listingData.StoriesTotal != ''
          ? listingData.StoriesTotal
          : propertyData && propertyData.StoriesTotal != ''
            ? propertyData.StoriesTotal
            : null,

      MonthsBack: null,

      StreetAddress:
        listingData && listingData.UnparsedAddress != ''
          ? listingData.UnparsedAddress
          : propertyData?.StreetAddress
            ? propertyData.StreetAddress
            : propertyData?.UnparsedAddress
              ? propertyData.UnparsedAddress
              : null,
      City:
        listingData && listingData.City
          ? listingData.City
          : propertyData && propertyData.City
            ? propertyData.City
            : null,
      StateOrProvince:
        listingData && listingData.StateOrProvince
          ? listingData.StateOrProvince
          : propertyData && propertyData.StateOrProvince
            ? propertyData.StateOrProvince
            : null,
      PostalCode:
        listingData && listingData.PostalCode
          ? listingData.PostalCode
          : propertyData && propertyData.PostalCode
            ? propertyData.PostalCode
            : null,

      valueTarget: valueTarget,
      MlsBoard:
        listingData && listingData.MlsBoard
          ? listingData.MlsBoard
          : propertyData && propertyData.Lst_MlsBoard
            ? propertyData.Lst_MlsBoard
            : null,
    };

    if (flag == 'cma') {
      this.cmaState.propertyDetailsValue = propertyDetails;
      // this.cmaState.listPropertySetCount = 1;
      this.cmaState.isLoadingCreateReport = false;
    } else {
      this.reportsState.listPropertySetValue = propertyDetails;
      this.reportsState.listPropertySetCount = 1;
      this.reportsState.isLoadingCreateReport = false;
    }
  }

  openSnackbar(upgradeMsg, success = false) {
    this.upgradeSnackbar = this.snackbar.open(upgradeMsg, 'Dismiss', {
      duration: 5000,
      panelClass: [success ? 'snackbar-success' : 'snackbar-error'],
      verticalPosition: 'top',
    });
  }

  calculateDateDifference(date1: string): number {
    const parsedDate1 = new Date(date1);
    const parsedDate2 = new Date();

    const differenceInMs = Math.abs(parsedDate1.getTime() - parsedDate2.getTime());
    const differenceInDays = differenceInMs / (1000 * 3600 * 24);

    return differenceInDays;
  }

  updateValueReport(params): Observable<{ isLoading?: boolean; data?: any }> {
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.ReportsApi.updateValueReport(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            observer.next({ data: res });
          },
          (err) => {
            observer.next({ data: null });
          }
        );
    });
  }

  shareReport(params): Observable<{ isLoading?: boolean; data?: any }> {
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.ReportsApi.shareReport(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            observer.next({ data: res });
          },
          (err) => {
            observer.next({ data: null });
          }
        );
    });
  }

  shareReportToLoanOfficer(params): Observable<{ isLoading?: boolean; data?: any }> {
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.ReportsApi.shareReportToLoanOfficer(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            observer.next({ data: res });
          },
          (err) => {
            observer.next({ data: null });
          }
        );
    });
  }
}
